import type { GlAccount, SalesSplit } from '../types';
import { sideForAccountType } from './clientTemplate';

/**
 * The client's chart, as the G/L Mappings screen uses it (docs/client-chart-and-sales-split.md §1–§2).
 * The server is the authority on every rule here (`GlChartRules`); these are the same rules said early, so the
 * bookkeeper sees a problem beside the field instead of after Save.
 */

/** Xero's account types, the same list as `GlAccountTypes.All` on the server. */
export const GL_ACCOUNT_TYPES = [
  'REVENUE', 'SALES', 'OTHERINCOME', 'DIRECTCOSTS', 'EXPENSE', 'OVERHEADS', 'DEPRECIATN',
  'BANK', 'CURRENT', 'INVENTORY', 'PREPAYMENT', 'FIXED', 'NONCURRENT',
  'CURRLIAB', 'LIABILITY', 'TERMLIAB', 'EQUITY',
] as const;

const sameCode = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

export function findAccount(accounts: readonly GlAccount[], code: string): GlAccount | undefined {
  return code.trim() ? accounts.find((a) => sameCode(a.code, code)) : undefined;
}

/** What a person sees for an account: their own name for it, else its name. */
export const displayName = (a: GlAccount): string => a.nameOverride?.trim() || a.name?.trim() || '';

/**
 * The chart in Xero's order (ruled 2026-10-29, docs/client-journal-template.md §B4): by code, numbers in number order,
 * then by name. A copy; the stored order is not the screen's.
 */
export function sortedChart(accounts: readonly GlAccount[]): GlAccount[] {
  return [...accounts].sort(
    (x, y) => x.code.localeCompare(y.code, undefined, { numeric: true, sensitivity: 'base' })
      || displayName(x).localeCompare(displayName(y), undefined, { sensitivity: 'base' }),
  );
}

/** Used for G/L work (wave 5 §1.4): null or absent is used, so an account stored before the flag stays offered. */
export const usedForGl = (a: GlAccount): boolean => a.usedForGl !== false;

/**
 * The accounts a mapping row may pick: active ones, in code order. Archived accounts are hidden, never deleted. By default
 * only the accounts used for G/L work (wave 5 §1.4); `showAll` lists the rest too. A picker keeps the code it already
 * holds visible on its own, so a mapping never silently disappears.
 */
export function pickable(accounts: readonly GlAccount[], showAll = false): GlAccount[] {
  return accounts
    .filter((a) => a.status !== 'ARCHIVED' && (showAll || usedForGl(a)))
    .sort((x, y) => x.code.localeCompare(y.code, undefined, { numeric: true }));
}

/** How many active accounts the default picker leaves out ("Show all accounts (3 more)"). */
export const hiddenFromPicker = (accounts: readonly GlAccount[]): number =>
  accounts.filter((a) => a.status !== 'ARCHIVED' && !usedForGl(a)).length;

/**
 * An account type's normal side (wave 5 §1.5): the rule wave 4 §2 already applies when an account is picked
 * (`sideForAccountType`, the same list as `GlAccountTypes.NormallyDebit` on the server). Null with no type.
 */
export const normalSide = sideForAccountType;

/**
 * The account a line posts to is normally on the OTHER side (wave 5 §1.5): its normal side, when it disagrees, or null. A
 * note on the line, never a refusal: a refund, a reversal or cash paid out is posted against the normal side on purpose.
 */
export function sideMismatch(account: GlAccount | undefined, side: 'debit' | 'credit'): 'debit' | 'credit' | null {
  const normal = normalSide(account?.type);
  return normal && normal !== side ? normal : null;
}

/**
 * The chart to save: the chart as edited, plus every code a mapping row names that is not in it yet, added as `typed`
 * with no name (§2: "type a code" for an account not in the chart adds it).
 */
export function chartWithMappedCodes(accounts: readonly GlAccount[], codes: Iterable<string>): GlAccount[] {
  const result = [...accounts];
  for (const raw of codes) {
    const code = raw.trim();
    if (code && !findAccount(result, code)) result.push({ code, status: 'ACTIVE', source: 'typed' });
  }
  return result;
}

/** Why a new account cannot be added, or null. The same two refusals the server makes (`GlChartRules`). */
export function newAccountProblem(accounts: readonly GlAccount[], code: string): 'codeRequired' | 'duplicate' | null {
  if (!code.trim()) return 'codeRequired';
  return findAccount(accounts, code) ? 'duplicate' : null;
}

/**
 * A key for a client-defined mapping row: `custom-` plus a slug of its label, made unique against the keys in use.
 * The key is stable once saved; the label is what a person reads and may change.
 */
export function customRoleKey(label: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  const slug = label
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'row';
  let key = `custom-${slug}`;
  for (let n = 2; used.has(key); n++) key = `custom-${slug}-${n}`;
  return key;
}

export const MAX_ROW_LABEL = 80;

/** "4010 38 % · 4020 7 %", in the screen's language: how a remembered sales split is said (§3, "shown as applied"). */
export function describeSplit(split: SalesSplit, locale: string): string {
  const pct = new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 2 });
  return split.shares.map((s) => `${s.code} ${pct.format(s.percent / 100)}`).join(' · ');
}

/** The day a split was learned, in the screen's language. */
export function splitLearnedOn(split: SalesSplit, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(split.learnedAt));
}
